import { LocalDate } from "@js-joda/core";
import React from "react";
import { Page } from "../activities/AppPage";
import { AddressComponents, PatientGender, PatientId, PatientTaskInstanceId } from "../messages";
import { TaskDayTimeParams } from "../messages/PatientTasks";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import AvailableTaskAddress from "./AvailableTask/components/AvailableTaskAddress";
import AvailableTaskDurationMinutes from "./AvailableTask/components/AvailableTaskDurationMinutes";
import AvailableTaskTimeFrame from "./AvailableTask/components/AvailableTaskTimeFrame";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "./Generic/AppCard";
import { AppList } from "./Generic/AppList";
import { IsDefined } from "./Generic/IsDefined";
import PatientInformationAppListItem from "./PatientInformationAppListItem";
import LinkedPatientAppListItem from "./LinkedPatientAppListItem";
import { AppButton } from "./Generic/AppButton";
import ons from "onsenui";

interface Props {
  dayTimeParams: TaskDayTimeParams;
  onClickCreatePatientNote: (note: string) => void;
  startDate: LocalDate;
  dueDate: LocalDate;
  durationMinutes: number | null;
  patientAddress: string | null;
  patientAddressInstructions: string | null;
  patientAddressComponents: AddressComponents | null;
  patientAge: number | null;
  patientGender: PatientGender | null;
  patientName: string;
  patientId: PatientId;
  patientTaskInstanceId: PatientTaskInstanceId;
  linkedPatientName: string | null;
  linkedPatientTaskInstanceId: PatientTaskInstanceId | null;
  onClickLocation: () => void;
  onRequestNavigate: (page: Page) => void;
  onClickLinkedPatient: () => void;
}

const TaskInstanceDetails = (props: Props) => {
  async function handleClickCreatePatientNote() {
    try {
      const promptResult = await ons.notification.prompt(
        translate(STRINGS.ADD_NOTE_TO_PATIENT_PROFILE_PLACEHOLDER),
        {
          animation: "none",
          modifier: "material",
          cancelable: true,
          title: translate(STRINGS.ADD_NOTE_TO_PATIENT_PROFILE_TITLE),
        }
      );

      // Check if input is empty
      const noteText = String(promptResult).trim();
      if (noteText === "") {
        return;
      }

      props.onClickCreatePatientNote(noteText);
    } catch (error) {
      console.error("Failed to add note:", error);
      ons.notification.alert(translate(STRINGS.ADD_NOTE_TO_PATIENT_PROFILE_ERROR_MESSAGE));
    }
  }

  return (
    <div className="AvailableTaskDetails">
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>{translate(STRINGS.VISIT_DETAILS)}</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <AppList>
            <AvailableTaskTimeFrame
              dayTimeParams={props.dayTimeParams}
              startDate={props.startDate}
              dueDate={props.dueDate}
            />

            <IsDefined value={props.durationMinutes}>
              {(durationMinutes) => (
                <AvailableTaskDurationMinutes durationMinutes={durationMinutes} />
              )}
            </IsDefined>

            <AvailableTaskAddress
              addressString={props.patientAddress}
              addressComponents={props.patientAddressComponents}
              addressInstructions={props.patientAddressInstructions}
              onClickLocation={props.onClickLocation}
            />
            {props.linkedPatientName !== null && props.linkedPatientTaskInstanceId !== null && (
              <LinkedPatientAppListItem
                linkedPatientName={props.linkedPatientName}
                onClickOpen={props.onClickLinkedPatient}
              />
            )}

            <PatientInformationAppListItem
              hasAccessToPatient={true}
              patientId={props.patientId}
              patientAge={props.patientAge}
              patientGender={props.patientGender}
              patientName={props.patientName}
              onRequestNavigate={props.onRequestNavigate}
            />
          </AppList>
          <AppButton block onClick={handleClickCreatePatientNote}>
            {translate(STRINGS.ADD_NOTE_TO_PATIENT_PROFILE_BUTTON)}
          </AppButton>
        </AppCardContent>
      </AppCard>
    </div>
  );
};

export default TaskInstanceDetails;
